// src/components/home/ProcessGraphic.jsx
import React from "react";
import {
  Box,
  Container,
  Wrap,
  WrapItem,
  VStack,
  Heading,
  Text,
  Circle,
  Icon,
  HStack,
  Button,
} from "@chakra-ui/react";
import {
  FileText,
  Calculator,
  Search,
  MessageSquare,
  Handshake,
  ShieldCheck,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

export default function ProcessGraphic({
  header = {
    title: "What you can do here",
    subtitle:
      "Explore success stories, estimate programs, dive into company coverage, share your experience, or connect with a provider.",
  },
  items = [
    {
      icon: FileText,
      title: "Read case studies",
      text: "Real outcomes and play-by-plays from people like you—what was promised, what actually happened, and how long it took.",
      gradient: "linear(to-br, teal.400, green.400)",
      to: "/tax-relief-blog",
    },
    {
      icon: Calculator,
      title: "Plan with the calculator",
      text: "Get a quick read on CNC, installment plans, or a ballpark OIC outlook before you talk to anyone.",
      gradient: "linear(to-br, purple.400, pink.400)",
      to: "/tax-relief-calculator",
    },
    {
      icon: Search,
      title: "Deep-dive company profiles",
      text: "Neutral summaries of focus areas, fees, disclosures, and responsiveness—so you can compare apples to apples.",
      gradient: "linear(to-br, blue.400, cyan.400)",
      to: "/tax-relief-companies",
    },
    {
      icon: MessageSquare,
      title: "Share a review or comment",
      text: "Add your experience to help others. Your feedback can become a future case study (with permission).",
      gradient: "linear(to-br, orange.400, yellow.400)",
      to: "/tax-relief-blog", // or a dedicated /reviews route if you add one
    },
    {
      icon: Handshake,
      title: "Get matched to a provider",
      text: "Tell us a few basics and we’ll route you to a firm that specializes in your kind of tax problem.",
      gradient: "linear(to-br, red.400, rose.400)",
      to: "/tax-relief-companies",
    },
    {
      icon: ShieldCheck,
      title: "Transparent methodology",
      text: "How we evaluate: focus & scope, transparency, and track record/response—weighted for time-sensitive IRS issues.",
      gradient: "linear(to-br, emerald.400, lime.400)",
      to: "/methodology",
    },
  ],
}) {
  const sub = { base: "gray.700", _dark: "gray.300" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };

  return (
    <Box
      position="relative"
      py={{ base: 12, md: 16 }}
      bg={{ base: "gray.50", _dark: "gray.900" }}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      overflow="hidden"
    >
      {/* soft background blobs for visual interest */}
      <Box
        position="absolute"
        top="-120px"
        left="-120px"
        w="360px"
        h="360px"
        rounded="full"
        bgGradient="radial(blue.200, transparent 60%)"
        opacity={0.35}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-140px"
        right="-140px"
        w="420px"
        h="420px"
        rounded="full"
        bgGradient="radial(pink.200, transparent 60%)"
        opacity={0.3}
        pointerEvents="none"
      />

      <Container maxW="6xl" position="relative" zIndex={1}>
        <VStack align="start" spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading size="lg">{header.title}</Heading>
          {header.subtitle ? <Text color={sub}>{header.subtitle}</Text> : null}
        </VStack>

        {/* Bubble flow: not a grid, flexible wrap with large circles */}
        <Wrap spacing={{ base: 6, md: 8 }} align="center">
          {items.map((it, i) => (
            <WrapItem key={i} flex="1 1 280px" maxW="520px">
              <HStack
                spacing={{ base: 4, md: 5 }}
                align="flex-start"
                p={{ base: 2, md: 3 }}
              >
                <Circle
                  size={{ base: "80px", md: "96px" }}
                  bgGradient={it.gradient}
                  shadow="md"
                  flexShrink={0}
                >
                  <Icon as={it.icon} boxSize={{ base: 8, md: 10 }} />
                </Circle>

                <VStack align="start" spacing={2}>
                  <Heading size="md">{it.title}</Heading>
                  <Text color={sub}>{it.text}</Text>
                </VStack>
              </HStack>
            </WrapItem>
          ))}
        </Wrap>

        {/* Optional page-level CTAs */}
      </Container>
    </Box>
  );
}
